import type { KYCQuestionDefinition } from '@anchorage/graphql/generated';
import type { CustomLinkProps } from '@anchorage/utils';
import { parseSanitize } from '@anchorage/utils';
import { FormRadioButtonGroup, reactHookForm, useWatch } from '@latitude/form';
import { Icon } from '@latitude/icons';
import { Text } from '@latitude/text';
import { Tooltip } from '@latitude/tooltip';
import cn from 'classnames';
import type { ComponentType, FC } from 'react';
import { useEffect, useMemo, useState } from 'react';

type Props = {
  question: KYCQuestionDefinition;
  isDisabled?: boolean;
  CustomLink?: ComponentType<CustomLinkProps>;
  trackFn?: (questionId: string) => void;
};

const RadioComponent: FC<Props> = ({
  question,
  isDisabled = false,
  CustomLink,
  trackFn = () => {},
}) => {
  const { control } = reactHookForm.useFormContext();

  // track change
  const [userClicked, setUserClicked] = useState(false);
  const [prevValue, setPrevValue] = useState<string | string[] | undefined>('');

  const selectedOption = useWatch({
    control,
    name: `${question.id}.options`,
  }) as string[] | string;
  useEffect(() => {
    setPrevValue(selectedOption);
    if (!userClicked) {
      return;
    }
    if (selectedOption !== prevValue) {
      trackFn?.(question.id);
    }
  }, [selectedOption, userClicked, question.id, prevValue, trackFn]);

  const hasSubtitles = question.options.some((opt) => opt.subtext);

  const options = useMemo(
    () =>
      question.options?.map((opt) => ({
        id: opt.id,
        value: opt.id,
        // Keeps the radio button on the first line of a multi-line label
        // instead of centering it next to the subtitle.
        labelProps: hasSubtitles ? { className: 'items-start' } : undefined,
        label: (
          <div className="flex items-center">
            <div>
              <Text>{opt.text}</Text>
              {opt.subtext ? (
                <Text variant="captionRegular" color="muted">
                  {opt.subtext}
                </Text>
              ) : null}
            </div>
            {opt.tooltip ? (
              <Tooltip
                text={
                  <Text
                    color="muted"
                    variant="captionRegular"
                    className="overflow-auto leading-relaxed [&>h4]:text-sys-size-14 [&>h4]:font-bold [&>h4]:text-color-ref-text-strong [&>ul]:my-spacing-sys-spacing-12 [&_ul]:ml-spacing-sys-spacing-16 [&_ul]:list-disc"
                  >
                    {parseSanitize(opt.tooltip)}
                  </Text>
                }
              >
                <Icon name="info-circle" className="h-sizing-sys-size-16" />
              </Tooltip>
            ) : null}
          </div>
        ),
        disabled: isDisabled,
      })),
    [question, isDisabled, hasSubtitles],
  );

  return (
    <div
      onClick={() => setUserClicked(true)}
      className={cn(
        'mt-spacing-sys-spacing-4 flex flex-col',
        hasSubtitles
          ? 'mb-spacing-sys-spacing-12 gap-sizing-sys-size-28'
          : 'gap-spacing-sys-spacing-8',
      )}
    >
      <FormRadioButtonGroup
        control={control}
        name={`${question.id}.options`}
        options={options ?? []}
        className={cn(
          'flex flex-col',
          hasSubtitles ? 'gap-sizing-sys-size-40' : 'gap-2',
        )}
      />
      {question.footer && (
        <Text
          variant="captionMedium"
          className="text-color-comp-text-field-helper-text-enabled"
        >
          {parseSanitize(question.footer, CustomLink)}
        </Text>
      )}
    </div>
  );
};

export { RadioComponent };
